import type { Face } from "./brep";
import { faceByThreePoints } from "./brep";
import { classifyContainmentVec2PolyLine2Fast } from "./containment";
import { polyLine2IsClosed } from "./polyLine2";
import type { PolyLine3 } from "./polyLine3";
import { polyLine3New, polyLine3ToPolyLine2IgnoreZ } from "./polyLine3";
import { vec3CrossNormalize, vec3Normalize, vec3Scale } from "./vec3";
import type { Vec3 } from "./vec3";

interface Surfel {
  position: Vec3;
  normal: Vec3;
  binormal: Vec3;
  tangent: Vec3;
  rotation: number;
}
/** Check if a Vec3 is valid (not NaN and has length 3) */
function isValidVec3(v: Vec3): boolean {
  return v && v.length === 3 && !isNaN(v[0]) && !isNaN(v[1]) && !isNaN(v[2]);
}

/**
 * Convert a polyline into a series of surfels that describe the surface
 */
function polylineToSurfels(polyline: PolyLine3, spacing: number = 0.1): Surfel[] {
  const surfels: Surfel[] = [];
  const vertexCount = polyline.length / 3;

  // First pass: create a base Surfel at each vertex
  for (let i = 0; i < vertexCount; i++) {
    const position: Vec3 = [polyline[i * 3], polyline[i * 3 + 1], polyline[i * 3 + 2]];

    // Skip invalid positions
    if (!isValidVec3(position)) {
      continue;
    }

    // Compute tangent
    let tangent: Vec3;
    if (i === vertexCount - 1 && i > 0) {
      // Last vertex - use previous edge
      tangent = [
        position[0] - polyline[(i - 1) * 3],
        position[1] - polyline[(i - 1) * 3 + 1],
        position[2] - polyline[(i - 1) * 3 + 2],
      ];
    } else if (i < vertexCount - 1) {
      // Use next edge
      tangent = [
        polyline[(i + 1) * 3] - position[0],
        polyline[(i + 1) * 3 + 1] - position[1],
        polyline[(i + 1) * 3 + 2] - position[2],
      ];
    } else {
      // Single-point polyline or invalid fallback
      continue;
    }

    // Normalize tangent
    const len = Math.sqrt(tangent[0] * tangent[0] + tangent[1] * tangent[1] + tangent[2] * tangent[2]);
    if (len > 0) {
      tangent = vec3Scale(tangent[0] / len, tangent[1] / len, tangent[2] / len, 1);
    } else {
      continue;
    }

    // Compute binormal (perp to tangent & up)
    const up: Vec3 = [0, 0, 1];
    const binormal = vec3CrossNormalize(tangent[0], tangent[1], tangent[2], up[0], up[1], up[2]);

    // Compute normal from binormal & tangent
    const normal = vec3CrossNormalize(binormal[0], binormal[1], binormal[2], tangent[0], tangent[1], tangent[2]);

    surfels.push({
      position,
      normal,
      binormal,
      tangent,
      rotation: 0,
    });
  }

  // Second pass: add intermediate surfels for better distribution
  const refinedSurfels: Surfel[] = [];
  for (let i = 0; i < surfels.length - 1; i++) {
    const current = surfels[i];
    const next = surfels[i + 1];

    refinedSurfels.push(current);

    // Distance between current and next
    const dx = next.position[0] - current.position[0];
    const dy = next.position[1] - current.position[1];
    const dz = next.position[2] - current.position[2];
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    // Insert extra points if distance > spacing
    const steps = Math.floor(distance / spacing);
    for (let j = 1; j < steps; j++) {
      const t = j / steps;
      refinedSurfels.push(interpolateSurfels(current, next, t));
    }
  }

  // Add the last surfel
  if (surfels.length > 0) {
    refinedSurfels.push(surfels[surfels.length - 1]);
  }

  return refinedSurfels;
}

/**
 * Interpolate between two surfels
 */
function interpolateSurfels(s1: Surfel, s2: Surfel, t: number): Surfel {
  // Interpolate position
  const position: Vec3 = [
    s1.position[0] + (s2.position[0] - s1.position[0]) * t,
    s1.position[1] + (s2.position[1] - s1.position[1]) * t,
    s1.position[2] + (s2.position[2] - s1.position[2]) * t,
  ];

  // Interpolate and normalize direction vectors
  const tangent = vec3Normalize(
    s1.tangent[0] + (s2.tangent[0] - s1.tangent[0]) * t,
    s1.tangent[1] + (s2.tangent[1] - s1.tangent[1]) * t,
    s1.tangent[2] + (s2.tangent[2] - s1.tangent[2]) * t,
  );

  const binormal = vec3Normalize(
    s1.binormal[0] + (s2.binormal[0] - s1.binormal[0]) * t,
    s1.binormal[1] + (s2.binormal[1] - s1.binormal[1]) * t,
    s1.binormal[2] + (s2.binormal[2] - s1.binormal[2]) * t,
  );

  const normal = vec3Normalize(
    s1.normal[0] + (s2.normal[0] - s1.normal[0]) * t,
    s1.normal[1] + (s2.normal[1] - s1.normal[1]) * t,
    s1.normal[2] + (s2.normal[2] - s1.normal[2]) * t,
  );

  // Interpolate rotation
  const rotation = s1.rotation + (s2.rotation - s1.rotation) * t;

  return { position, normal, binormal, tangent, rotation };
}

/**
 * Creates a smooth transition between two polylines using surfel interpolation
 * This is mostly an example function for bridging two polylines.
 */
export function interpolatePolyLines(line1: PolyLine3, line2: PolyLine3, t: number): PolyLine3 {
  const surfels1 = polylineToSurfels(line1);
  const surfels2 = polylineToSurfels(line2);

  // Use the line with fewer surfels as the reference for number of output points
  const referenceCount = Math.min(surfels1.length, surfels2.length);
  const result = polyLine3New(referenceCount * 3);

  for (let i = 0; i < referenceCount; i++) {
    const frac = i / (referenceCount - 1);

    // Get surfels from each line
    const s1 = surfels1[Math.floor(frac * (surfels1.length - 1))];
    const s2 = surfels2[Math.floor(frac * (surfels2.length - 1))];

    // Interpolate
    const interpolated = interpolateSurfels(s1, s2, t);

    // Store the interpolated position
    result[i * 3] = interpolated.position[0];
    result[i * 3 + 1] = interpolated.position[1];
    result[i * 3 + 2] = interpolated.position[2];
  }

  return result;
}

/**
 * Helper: compute bounding box for a set of polylines in X and Y.
 */
function computeXYBounds(polylines: PolyLine3[]): { minX: number; maxX: number; minY: number; maxY: number } {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const pl of polylines) {
    for (let i = 0; i < pl.length; i += 3) {
      const x = pl[i + 0];
      const y = pl[i + 1];
      if (x < minX) {
        minX = x;
      }
      if (x > maxX) {
        maxX = x;
      }
      if (y < minY) {
        minY = y;
      }
      if (y > maxY) {
        maxY = y;
      }
    }
  }

  return { minX, maxX, minY, maxY };
}

/**
 * Find the closest polyline point and its influence on a given position
 */
interface DeformedPoint {
  x: number;
  y: number;
  z: number;
  influence: number;
}

function findNearestPolylineInfluence(x: number, y: number, polylines: PolyLine3[]): DeformedPoint[] {
  const SNAP_THRESHOLD = 5; // Influence radius
  const MAX_POINTS = 3; // Number of influential points to consider

  // Collect all points with their distances
  const allPoints = polylines.flatMap((polyline) => {
    const points: DeformedPoint[] = [];
    for (let i = 0; i < polyline.length; i += 3) {
      const px = polyline[i];
      const py = polyline[i + 1];
      const pz = polyline[i + 2];

      const dist = Math.sqrt((x - px) * (x - px) + (y - py) * (y - py));
      if (dist < SNAP_THRESHOLD) {
        const influence = Math.max(0, 1 - dist / SNAP_THRESHOLD);
        points.push({ x: px, y: py, z: pz, influence });
      }
    }
    return points;
  });

  // Sort by influence and take top MAX_POINTS
  return allPoints.sort((a, b) => b.influence - a.influence).slice(0, MAX_POINTS);
}

/**
 * Compute deformed position that respects input polylines
 */
function computeDeformedPosition(
  x: number,
  y: number,
  polylines: PolyLine3[],
  sortedYs: number[],
): { x: number; y: number; z: number } {
  // Get base Z interpolation
  const baseZ = computeBaseZ(x, y, polylines, sortedYs);

  // Find nearest influential points
  const nearestPoints = findNearestPolylineInfluence(x, y, polylines);

  if (nearestPoints.length > 0) {
    // Normalize influences
    const totalInfluence = nearestPoints.reduce((sum, p) => sum + p.influence, 0);
    if (totalInfluence > 0) {
      const normalizedPoints = nearestPoints.map((p) => ({
        ...p,
        influence: p.influence / totalInfluence,
      }));

      // Compute weighted average position
      const deformed = normalizedPoints.reduce(
        (acc, p) => ({
          x: acc.x + p.x * p.influence,
          y: acc.y + p.y * p.influence,
          z: acc.z + p.z * p.influence,
        }),
        { x: 0, y: 0, z: 0 },
      );

      // Blend between original and deformed position based on total influence
      const blendFactor = Math.min(1, totalInfluence);
      return {
        x: x * (1 - blendFactor) + deformed.x * blendFactor,
        y: y * (1 - blendFactor) + deformed.y * blendFactor,
        z: baseZ * (1 - blendFactor) + deformed.z * blendFactor,
      };
    }
  }

  return { x, y, z: baseZ };
}

/**
 * Helper to compute base Z interpolation
 */
function computeBaseZ(x: number, y: number, polylines: PolyLine3[], sortedYs: number[]): number {
  // If only one polyline, just pick the nearest vertex in X
  if (polylines.length === 1) {
    return getZClosestX(x, polylines[0]);
  } else {
    // Find which two polylines bracket this y
    let lowerIndex = -1;
    let upperIndex = -1;
    for (let i = 0; i < sortedYs.length - 1; i++) {
      if (y >= sortedYs[i] && y <= sortedYs[i + 1]) {
        lowerIndex = i;
        upperIndex = i + 1;
        break;
      }
    }

    if (lowerIndex === -1 || upperIndex === -1) {
      const first = polylines[0];
      const last = polylines[polylines.length - 1];
      const avgYFirst = sortedYs[0];
      const avgYLast = sortedYs[sortedYs.length - 1];
      const distFirst = Math.abs(y - avgYFirst);
      const distLast = Math.abs(y - avgYLast);
      return distFirst < distLast ? getZClosestX(x, first) : getZClosestX(x, last);
    } else {
      const plLower = polylines[lowerIndex];
      const plUpper = polylines[upperIndex];
      const yLower = sortedYs[lowerIndex];
      const yUpper = sortedYs[upperIndex];
      const factor = (y - yLower) / (yUpper - yLower);
      const zLower = getZClosestX(x, plLower);
      const zUpper = getZClosestX(x, plUpper);
      return zLower + (zUpper - zLower) * factor;
    }
  }
}

/**
 * Find the Z in the polyline that is closest to the specified x (naive approach).
 * We simply step through the polyline's points (which is [x,y,z, x2,y2,z2,...])
 * and find the vertex with X coordinate nearest to the input x.
 */
function getZClosestX(targetX: number, polyline: PolyLine3): number {
  if (polyline.length < 3) {
    return 0;
  }
  let closestZ = polyline[2];
  let minDist = Math.abs(polyline[0] - targetX);

  for (let i = 3; i < polyline.length; i += 3) {
    const x = polyline[i];
    const z = polyline[i + 2];
    const dist = Math.abs(x - targetX);
    if (dist < minDist) {
      minDist = dist;
      closestZ = z;
    }
  }
  return closestZ;
}

/**
 * Find the closest point on a line segment to a target point
 */
function closestPointOnSegment(
  x: number,
  y: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): { x: number; y: number; t: number } {
  const ax = x2 - x1;
  const ay = y2 - y1;
  const len2 = ax * ax + ay * ay;

  if (len2 === 0) {
    return { x: x1, y: y1, t: 0 };
  }

  // Project point onto line segment
  let t = ((x - x1) * ax + (y - y1) * ay) / len2;
  t = Math.max(0, Math.min(1, t)); // Clamp to segment

  return {
    x: x1 + t * ax,
    y: y1 + t * ay,
    t,
  };
}

// Modify weight calculation to use inverse distance weighting (IDW)
function calculateWeight(dist: number, power: number = 2): number {
  if (dist === 0) {
    return 1000; // Large number for exact matches
  }
  // IDW formula: 1/d^p where p is typically 2
  return 1 / Math.pow(dist, power);
}

/**
 * Grid-based lofting algorithm for creating a surface from a set of polylines
 * @param contourPls - Array of polylines representing the contour of the surface
 * @param heightOverridePls - Array of polylines representing height overrides
 * @param gridSize - The size of the grid cells (default is 36)
 * @returns An array of faces representing the lofted surface
 */
export function gridLoft(
  contourPls: PolyLine3[],
  heightOverridePls: PolyLine3[],
  gridSize: number = 36,
): { faces: Face[]; edgePl3s: PolyLine3[]; lowestZ: number } {
  if (contourPls.length === 0) {
    return { faces: [], edgePl3s: [], lowestZ: 0 };
  }

  // 1) Compute bounding box from all polylines
  const { minX, maxX, minY, maxY } = computeXYBounds([...contourPls, ...heightOverridePls]);
  const width = maxX - minX;
  const height = maxY - minY;

  if (width <= 0 || height <= 0) {
    return { faces: [], edgePl3s: [], lowestZ: 0 };
  }

  // 2) Determine grid resolution
  const xCount = Math.max(2, Math.ceil(width / gridSize));
  const yCount = Math.max(2, Math.ceil(height / gridSize));
  const dx = width / (xCount - 1);
  const dy = height / (yCount - 1);

  // Pre-compute sorted Y values for interpolation (using contour polylines only)
  const sortedYs = contourPls.map((pl) => averageY(pl)).sort((a, b) => a - b);

  // 3) Create grid points with base surface from contours only
  const gridPoints: Array<Array<{ x: number; y: number; z: number }>> = [];

  for (let yi = 0; yi < yCount; yi++) {
    const row: Array<{ x: number; y: number; z: number }> = [];
    const baseY = minY + yi * dy;

    for (let xi = 0; xi < xCount; xi++) {
      const baseX = minX + xi * dx;
      let totalWeight = 0;
      let weightedX = 0;
      let weightedY = 0;
      let weightedZ = 0;

      // Only use contour polylines for base surface
      for (const polyline of contourPls) {
        for (let i = 0; i < polyline.length - 3; i += 3) {
          const x1 = polyline[i];
          const y1 = polyline[i + 1];
          const z1 = polyline[i + 2];
          const x2 = polyline[i + 3];
          const y2 = polyline[i + 4];
          const z2 = polyline[i + 5];

          const closest = closestPointOnSegment(baseX, baseY, x1, y1, x2, y2);
          const dist = Math.sqrt((baseX - closest.x) * (baseX - closest.x) + (baseY - closest.y) * (baseY - closest.y));
          const weight = calculateWeight(dist);
          const z = z1 + (z2 - z1) * closest.t;

          weightedX += closest.x * weight;
          weightedY += closest.y * weight;
          weightedZ += z * weight;
          totalWeight += weight;
        }
      }

      if (totalWeight > 0) {
        const blendFactor = Math.min(1, totalWeight);
        row.push({
          x: baseX * (1 - blendFactor) + (weightedX / totalWeight) * blendFactor,
          y: baseY * (1 - blendFactor) + (weightedY / totalWeight) * blendFactor,
          z: weightedZ / totalWeight,
        });
      } else {
        const deformed = computeDeformedPosition(baseX, baseY, contourPls, sortedYs);
        row.push({ x: baseX, y: baseY, z: deformed.z });
      }
    }
    gridPoints.push(row);
  }

  for (const polyline of contourPls) {
    if (polyLine2IsClosed(polyline)) {
      heightOverridePls.push(polyline);
    }
  }

  // 4) Apply pad heights after base surface is created
  const pads = heightOverridePls.map((polyline) => ({
    boundary: polyLine3ToPolyLine2IgnoreZ(polyline),
    zHeight: polyline[2], // Take Z from first point
  }));

  let lowestZ = Number.POSITIVE_INFINITY;

  // Adjust heights for points within pads
  for (let yi = 0; yi < yCount; yi++) {
    for (let xi = 0; xi < xCount; xi++) {
      const point = gridPoints[yi][xi];

      for (const pad of pads) {
        if (classifyContainmentVec2PolyLine2Fast(point.x, point.y, pad.boundary)) {
          point.z = pad.zHeight;
          break;
        }
      }

      if (point.z < lowestZ) {
        lowestZ = point.z;
      }
    }
  }

  // 5) Generate faces from adjusted grid points
  const faces: Face[] = [];
  for (let yi = 0; yi < yCount - 1; yi++) {
    for (let xi = 0; xi < xCount - 1; xi++) {
      const p00 = gridPoints[yi][xi];
      const p10 = gridPoints[yi][xi + 1];
      const p01 = gridPoints[yi + 1][xi];
      const p11 = gridPoints[yi + 1][xi + 1];

      const face1 = faceByThreePoints(p00.x, p00.y, p00.z, p10.x, p10.y, p10.z, p01.x, p01.y, p01.z);
      const face2 = faceByThreePoints(p10.x, p10.y, p10.z, p11.x, p11.y, p11.z, p01.x, p01.y, p01.z);

      if (face1) {
        faces.push(face1);
      }
      if (face2) {
        faces.push(face2);
      }
    }
  }

  // 6) Build open edge polylines for each perimeter edge of the grid
  // Top edge: left -> right
  const topPl3 = polyLine3New(xCount * 3);
  {
    let wi = 0;
    for (let xi = 0; xi < xCount; xi++) {
      const p = gridPoints[0][xi];
      topPl3[wi++] = p.x;
      topPl3[wi++] = p.y;
      topPl3[wi++] = p.z;
    }
  }

  // Right edge: top -> bottom
  const rightPl3 = polyLine3New(yCount * 3);
  {
    let wi = 0;
    for (let yi = 0; yi < yCount; yi++) {
      const p = gridPoints[yi][xCount - 1];
      rightPl3[wi++] = p.x;
      rightPl3[wi++] = p.y;
      rightPl3[wi++] = p.z;
    }
  }

  // Bottom edge: right -> left
  const bottomPl3 = polyLine3New(xCount * 3);
  {
    let wi = 0;
    for (let xi = xCount - 1; xi >= 0; xi--) {
      const p = gridPoints[yCount - 1][xi];
      bottomPl3[wi++] = p.x;
      bottomPl3[wi++] = p.y;
      bottomPl3[wi++] = p.z;
    }
  }

  // Left edge: bottom -> top
  const leftPl3 = polyLine3New(yCount * 3);
  {
    let wi = 0;
    for (let yi = yCount - 1; yi >= 0; yi--) {
      const p = gridPoints[yi][0];
      leftPl3[wi++] = p.x;
      leftPl3[wi++] = p.y;
      leftPl3[wi++] = p.z;
    }
  }

  const edgePl3s = [topPl3, rightPl3, bottomPl3, leftPl3];

  if (!isFinite(lowestZ)) {
    lowestZ = 0;
  }

  return { faces, edgePl3s, lowestZ };
}

/** Helper to find average Y in a given polyline */
function averageY(polyline: PolyLine3): number {
  if (polyline.length < 3) {
    return 0;
  }
  let sum = 0;
  let count = 0;
  for (let i = 1; i < polyline.length; i += 3) {
    sum += polyline[i];
    count++;
  }
  return count > 0 ? sum / count : 0;
}
